import { StButton } from "@storytell/ui/buttons";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import {
  type SortingState,
  type ColumnDef,
  createSolidTable,
  getCoreRowModel,
  getSortedRowModel,
  type Header,
  flexRender,
} from "@tanstack/solid-table";
import dayjs from "dayjs";
import { TbEye, TbCaretUp, TbCaretDown, TbMessage2, TbCheck } from "solid-icons/tb";
import { createSignal, onMount, Show, For, createEffect, onCleanup, createMemo } from "solid-js";
import { useWire } from "~/wire";
import { twMerge } from "tailwind-merge";
import { debounce } from "@solid-primitives/scheduled";
import type { Asset, Concept } from "@storytell/types";
import { Checkbox } from "@kobalte/core/checkbox";
import { TbStackPlus } from "../knowledge/TbStackPlus";
import type { EntitySelection } from "../knowledge/useEntitySelection";
import { dispatchGlobalEvent } from "@storytell/events";

export const NavigationConceptsPaneTable = (props: {
  search: string;
  buildFilterFunction: (item: Asset | Concept) => boolean;
  selection: EntitySelection;

  viewConceptDetails: (id: string) => void;
  viewConceptInGraph: (id: string) => void;
  selectedConcept: string;
  mount?: Node;
}) => {
  const wire = useWire();

  // Default sorting: by scope column, descending (in-scope first)
  const [sorting, setSorting] = createSignal<SortingState>([]);

  const concepts = createMemo(() => wire.sync.concepts.search(props.search).filter(props.buildFilterFunction));

  // Expanded rows state
  const [expandedRows, setExpandedRows] = createSignal<Record<string, boolean>>({});

  // Table columns definition
  const columns: ColumnDef<Concept>[] = [
    {
      id: "bulk-select",
      header: () => (
        <Checkbox
          checked={props.selection.allSelected(concepts())}
          indeterminate={props.selection.someSelected(concepts()) && !props.selection.allSelected(concepts())}
          aria-label={props.selection.allSelected(concepts()) ? "Deselect all" : "Select all"}
          onChange={(value) => props.selection.toggleAll(value, concepts())}
          class="flex items-center justify-center"
        >
          <Checkbox.Input />
          <Checkbox.Control
            data-some-selected={props.selection.someSelected(concepts()) && !props.selection.allSelected(concepts())}
            class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[some-selected=true]:bg-black/30 dark:data-[some-selected=true]:bg-white/30 data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors"
          >
            <Show when={props.selection.allSelected(concepts()) || props.selection.someSelected(concepts())}>
              <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
            </Show>
          </Checkbox.Control>
        </Checkbox>
      ),
      meta: {
        verticalAlign: "top",
      },
      cell: (info) => {
        const id = () => info.row.original.id;
        const checked = () => !!props.selection.selected()[id()];
        return (
          <Checkbox
            checked={checked()}
            aria-label={checked() ? "Deselect asset" : "Select asset"}
            onChange={(value) => {
              props.selection.setSelected((prev) => ({ ...prev, [id()]: value }));
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            class="flex items-center justify-center mt-2"
          >
            <Checkbox.Input />
            <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
              <Show when={checked()}>
                <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
              </Show>
            </Checkbox.Control>
          </Checkbox>
        );
      },
      footer: (props) => props.column.id,
      enableSorting: false,
    },
    {
      id: "actions",
      header: () => <div />,
      meta: {
        verticalAlign: "top",
      },
      cell: (info) => (
        <div class="flex flex-col gap-2">
          <StTooltip
            placement="top"
            portalProps={{ mount: props.mount }}
            contentProps={{ class: "z-50" }}
            content="View Concept details"
          >
            <StTooltipTrigger
              as={StButton}
              icon={TbEye}
              noColor
              class="bg-canvas hover:bg-canvas-hover whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                props.viewConceptDetails(info.row.original.id);
              }}
            >
              View details
            </StTooltipTrigger>
          </StTooltip>
          {/* <StTooltip
            portalProps={{ mount: props.mount }}
            placement="top"
            contentProps={{ class: "z-50" }}
            content="Chat with this Concept"
          >
            <StTooltipTrigger
              as={StButton}
              icon={TbMessage2}
              noColor
              class="bg-canvas hover:bg-canvas-hover whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                dispatchGlobalEvent("start-prompt-with-mention", { id: info.row.original.id });
                actions.closeSecondaryPane();
              }}
            >
              Chat with{" "}
            </StTooltipTrigger>
          </StTooltip> */}
        </div>
      ),
    },
    // {
    //   id: "bulk-select",
    //   header: () => (
    //     <Checkbox
    //       checked={allSelected()}
    //       indeterminate={someSelected() && !allSelected()}
    //       aria-label={allSelected() ? "Deselect all" : "Select all"}
    //       onChange={toggleAll}
    //       class="flex items-center justify-center"
    //     >
    //       <Checkbox.Input />
    //       <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
    //         <Show when={allSelected() || (someSelected() && !allSelected())}>
    //           <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
    //         </Show>
    //       </Checkbox.Control>
    //     </Checkbox>
    //   ),
    //   cell: (info) => {
    //     const id = () => info.row.original.id;
    //     const checked = () => !!selected()[id()];
    //     return (
    //       <Checkbox
    //         checked={checked()}
    //         aria-label={checked() ? "Deselect concept" : "Select concept"}
    //         onChange={(value) => {
    //           setSelected((prev) => ({ ...prev, [id()]: value }));
    //         }}
    //         class="flex items-center justify-center"
    //       >
    //         <Checkbox.Input />
    //         <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
    //           <Show when={checked()}>
    //             <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
    //           </Show>
    //         </Checkbox.Control>
    //       </Checkbox>
    //     );
    //   },
    //   footer: (props) => props.column.id,
    //   enableSorting: false,
    // },
    {
      header: "Concept Name",
      accessorFn: (row) => row.name,
      meta: {
        verticalAlign: "top",
      },
      cell: (info) => {
        let btn!: HTMLButtonElement;
        onMount(() => {
          const leave = debounce(() => {
            const parent = btn.parentElement;
            if (!parent) return;
            parent.style.removeProperty("max-width");
          }, 300);
          const enter = () => {
            const parent = btn.parentElement;
            if (!parent) return;
            leave.clear();
            const width = parent.getBoundingClientRect().width - 38;
            parent.style.setProperty("max-width", `${width}px`);
          };

          btn.addEventListener("mouseenter", enter);
          btn.addEventListener("mouseleave", leave);
          onCleanup(() => {
            btn.removeEventListener("mouseenter", enter);
            btn.removeEventListener("mouseleave", leave);
          });
        });
        return (
          <button
            ref={btn}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              dispatchGlobalEvent("add-items-to-prompt-scope", { ids: [info.row.original.id] });
            }}
            type="button"
            class="group/btn relative min-w-64 text-left cursor-pointer font-medium text-primary-text max-w-full line-clamp-1 wrap-anywhere py-1 px-2 border border-transparent rounded-lg hover:border-primary-border transition-all"
          >
            <span class="absolute left-2 opacity-0 group-hocus/btn:opacity-100 transition-all duration-100">
              Chat with
            </span>
            <span class="group-hocus/btn:ml-17 transition-all duration-100 text-transparent bg-clip-text bg-primary-text group-hocus/btn:bg-gradient-to-r group-hocus/btn:from-violet-600 group-hocus/btn:to-blue-600 group-hocus/btn:dark:from-violet-400 group-hocus/btn:dark:to-blue-400">
              <span class="hidden group-hocus/btn:inline">@</span>
              {String(info.getValue())}
            </span>
          </button>
        );
      },
      footer: (props) => props.column.id,
    },
    {
      header: "Description",
      cell: (info) => {
        const conceptId = () => info.row.original.id;
        const isExpanded = () => !!expandedRows()[conceptId()] || props.selectedConcept === conceptId();
        let ref!: HTMLDivElement;
        const [clamped, setClamped] = createSignal(false);

        onMount(() => {
          if (ref.scrollHeight > ref.clientHeight) {
            setClamped(true);
          }
        });
        return (
          <div class="space-y-2 relative" classList={{ "overflow-hidden max-h-18": !isExpanded() }} ref={ref}>
            <p class="text-xs font-medium text-primary-text">{info.row.original.summary}</p>
            <p class="text-xs">{info.row.original.description}</p>
            <Show when={clamped() && !isExpanded()}>
              <div class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-panel group-hover/row:from-tile-hover transition-colors to-transparent" />
            </Show>
          </div>
        );
      },
      footer: (props) => props.column.id,
    },
    {
      header: "Last Updated",
      meta: {
        verticalAlign: "top",
      },
      cell: (info) => {
        return <span class="text-xs">{dayjs(info.row.original.modifiedAt).format("LLL")}</span>;
      },
      footer: (props) => props.column.id,
    },
  ];

  // Create the table instance
  const table = createSolidTable({
    get data() {
      return concepts();
    },
    columns,
    state: {
      get sorting() {
        return sorting();
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });
  let tableRef!: HTMLTableElement;

  createEffect(() => {
    const conceptId = props.selectedConcept;
    if (tableRef && conceptId) {
      const row = tableRef.querySelector(`[data-concept="${conceptId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  });

  // Keyboard handler for sorting
  function handleHeaderKeyDown(e: KeyboardEvent, header: Header<Concept, unknown>) {
    if (header.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    }
  }

  // let inputRef!: HTMLInputElement;
  // onMount(() => {
  //   inputRef.focus();
  // });

  return (
    <>
      <div class="flex items-center gap-3 mb-2">
        <Show when={props.selection.selectedIds().length}>
          {(len) => (
            <>
              <StButton
                noColor
                class="shrink-0 bg-brand hover:bg-brand-hover text-on-brand"
                icon={TbMessage2}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatchGlobalEvent("add-items-to-prompt-scope", { ids: props.selection.selectedIds() });
                }}
              >
                Chat with {len()} Concept{len() === 1 ? "" : "s"}
              </StButton>
            </>
          )}
        </Show>
      </div>
      <div class="rounded-lg border border-primary-border max-h-full overflow-y-auto">
        <table class="min-w-full max-w-full text-sm" ref={tableRef}>
          <thead class="sticky top-0 bg-canvas border-b border-primary-border z-10">
            <For each={table.getHeaderGroups()}>
              {(headerGroup) => (
                <tr>
                  <For each={headerGroup.headers}>
                    {(header) => (
                      <th
                        colSpan={header.colSpan}
                        class="text-xs px-2 py-2 font-medium text-left text-primary-text whitespace-nowrap"
                      >
                        <Show when={!header.isPlaceholder}>
                          <div
                            class={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center gap-1"
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            role={header.column.getCanSort() ? "button" : undefined}
                            aria-pressed={header.column.getIsSorted() ? "true" : "false"}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => handleHeaderKeyDown(e as KeyboardEvent, header)}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <Show when={header.column.getIsSorted() === "asc"}>
                              <TbCaretUp class="inline-block w-4 h-4 text-brand" />
                            </Show>
                            <Show when={header.column.getIsSorted() === "desc"}>
                              <TbCaretDown class="inline-block w-4 h-4 text-brand" />
                            </Show>
                          </div>
                        </Show>
                      </th>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </thead>
          <tbody>
            <Show
              when={table.getRowModel().rows.length > 0}
              fallback={
                <tr>
                  <td colSpan={columns.length} class="text-center p-4 text-muted-text">
                    No concepts found
                  </td>
                </tr>
              }
            >
              <For each={table.getRowModel().rows}>
                {(row) => {
                  const conceptId = () => row.original.id;
                  // const isExpanded = () => !!expandedRows()[conceptId()];
                  return (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                    <tr
                      data-concept={conceptId()}
                      class={twMerge(
                        "group/row hover:bg-tile-hover transition-colors cursor-pointer scroll-mt-8",
                        props.selectedConcept === conceptId() && "bg-brand/10 hover:bg-brand/20",
                      )}
                      onClick={() => {
                        props.viewConceptInGraph(conceptId());
                        // setExpandedRows((prev) => ({
                        //   ...prev,
                        //   [conceptId()]: !isExpanded(),
                        // }));
                      }}
                    >
                      <For each={row.getVisibleCells()}>
                        {(cell) => (
                          <td
                            class="px-3 py-3 [&:not:last-child]:border-b border-muted-border text-secondary-text"
                            classList={{
                              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                              "align-top": (cell.column.columnDef.meta as any)?.verticalAlign === "top",
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        )}
                      </For>
                    </tr>
                  );
                }}
              </For>
            </Show>
          </tbody>
        </table>
      </div>
    </>
  );
};
